import { Stack } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { ICollection } from '../../api';
import { useAuthUser } from '../../api';
import { useTeamEmptyState } from '../../api/hooks/team/emptyStates';
import { entityModalStore } from '../../components/EntityModal/store';

import type { Filter } from '@repo/api-codegen';
import type { EmptyStateButtons } from '@repo/common/components/NewEmptyState/EmptyState';
import { EmptyState } from '@repo/common/components/NewEmptyState/EmptyState';
import { EntityType } from '@repo/common/enums/entityType';
import type {
	FetchModelInfiniteListHook,
	FetchModelList,
} from '../../api/factories/types';
import {
	fetchCatalogList,
	useCatalogInfiniteList,
} from '../../api/hooks/resourceCatalog';
import { useCatalogFilter } from '../../components/Filter/useCatalogFilter';
import { TableV2 } from '../../components/TableV2';
import type { OnCellClickHandlerParams } from '../../components/TableV2/types';
import { useEntityBreadcrumbTeam } from '../../hooks/useEntityBreadcrumbs';
import { useHomepageTeam } from '../HomePage/hooks/useHomepageTeam';
import { useStyles } from '../TeamCatalogPage/TeamCatalogPage.styles';
import { useActions, useColumns } from './TeamCollectionsPage.hooks';

const COLUMN_VISIBILITY = {
	catalogType: EntityType.document,
	catalogServerType: EntityType.document,
} as const;

const DEFAULT_CATALOG_FILTERS = {
	operands: [
		{
			operator: 'not',
			field: 'collections',
			operands: [],
		},
	],
} as { readonly operands: readonly Filter[] };

const QUICK_ACTIONS = [
	'actions::collections',
	'actions::pii',
	'actions::verified',
	'actions::sidebar',
	'actions::ai',
	'actions::delete',
] as const;

const DEFAULT_SEARCH_PARAMS_NESTING = {
	entity_type: EntityType.collection,
	calculate_children_count: true,
} as const;

const PAGINATION_LIST_OPTIONS = {
	suspense: true,
};

const WITH_CUSTOM_PROPERTIES = [EntityType.collection];

function TeamCollectionsPage() {
	const { teamId } = useParams();
	const { classes } = useStyles();

	const { user, isAdminUser } = useAuthUser();
	const { isTeamEditorUser } = useHomepageTeam();

	const columns = useColumns();
	const actions = useActions();

	const navigate = useNavigate();

	const { setTeamBreadcrumb } = useEntityBreadcrumbTeam();

	useEffect(() => {
		// If the user opens a entity page from this context we want the team
		// breadcrumb to match the team of this page.
		if (teamId) {
			setTeamBreadcrumb(teamId);
		}
	}, [setTeamBreadcrumb, teamId]);

	const handleNewCollection = useCallback(async () => {
		await entityModalStore.quickCreateEntity(
			EntityType.collection,
			'Untitled',
			[teamId ?? ''],
			{
				owners: [user.id],
			},
			navigate
		);
	}, [teamId, user.id, navigate]);

	const emptyState = useTeamEmptyState(teamId as string, 'collection');

	const buttons: EmptyStateButtons = [
		{
			label: 'Learn more',
			variant: 'default',
			onClick: () => {
				window.open('https://docs.secoda.com/features/collections-1', '_blank');
			},
		},
		{
			label: 'Create collection',
			variant: 'primary',
			onClick: handleNewCollection,
			requiresUseButtonPermission: true,
		},
	];

	const handleCellClick = useCallback(
		({ column, record }: OnCellClickHandlerParams<ICollection>) => {
			if (column.navigate !== false) {
				navigate(`/collections/${record.id}`);
			}
		},
		[navigate]
	);

	const defaultSearchParams = useMemo(
		() => ({
			team_id: teamId,
			entity_type: EntityType.collection,
			calculate_children_count: true,
		}),
		[teamId]
	);

	const filterOptions = useCatalogFilter();

	if (emptyState?.is_empty) {
		return (
			<EmptyState
				heading="Welcome to Collections"
				body="Curate relevant data and resources in collections for easy sharing and discoverability across your team"
				illustrationName="collections"
				buttons={buttons}
				hasUseButtonPermission={isTeamEditorUser || isAdminUser}
			/>
		);
	}

	return (
		<Stack className={classes.wrapper}>
			<TableV2<ICollection>
				withCustomProperties={WITH_CUSTOM_PROPERTIES}
				withQuickActions={QUICK_ACTIONS}
				nestingFilter="collections"
				withCsvExport
				withCheckbox
				withInteractiveHeader
				withSearch
				withFilterOptions={filterOptions}
				columns={columns}
				withActions={actions}
				onCellClick={handleCellClick}
				withInfiniteScroll
				fetchPaginationList={
					fetchCatalogList as unknown as FetchModelList<ICollection>
				}
				usePaginationList={
					useCatalogInfiniteList as FetchModelInfiniteListHook<ICollection>
				}
				columnVisibility={COLUMN_VISIBILITY}
				defaultRequiredSearchParams={defaultSearchParams}
				defaultRequiredSearchParamsNesting={DEFAULT_SEARCH_PARAMS_NESTING}
				defaultRequiredCatalogFilters={DEFAULT_CATALOG_FILTERS}
				usePaginationListOptions={PAGINATION_LIST_OPTIONS}
			/>
		</Stack>
	);
}

const TeamCollectionsPageWithObserver = observer(TeamCollectionsPage);
export default TeamCollectionsPageWithObserver;
